import React from 'react';
import EndpointCard from '../EndpointCard';
import CodeBlock from '../CodeBlock';

const Projects = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <section className="section">
      <h2 className="section-title">Projects</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
        Projects are containers for organizing related work and outputs. Create a project to group related
        resources, manage generation workflows, and enable access control and collaboration for teams working together.
        Projects support role-based sharing with owners and members, allowing you to collaborate securely with your team.
      </p>

      <EndpointCard
        method="POST"
        path="/projects/"
        title="Create Project"
        description="Creates a new project container. The current user automatically becomes the owner of the project."
        tags={['Authentication: Required']}
        requestBody={{
          name: "My Project"
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "name": "My Project"
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>name</strong> (required): The name of the project
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/projects/" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My Project"
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (201 Created):</h4>
            <CodeBlock code={`{
  "uuid": "{project_uuid}",
  "name": "My Project",
  "owner_user_uuid": "{user_uuid}",
  "created_at": "2024-01-15T10:00:00Z"
}`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/projects/"
        title="List Projects"
        description="Retrieves all projects where the current user is owner or member. Supports pagination with skip and limit parameters."
        tags={['Authentication: Required', 'Pagination: Supported']}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Query Parameters:</h4>
            <p className="text-xs text-gray-400">
              <strong>skip</strong> (optional, default: 0): Number of projects to skip<br/>
              <strong>limit</strong> (optional, default: 100): Maximum number of projects to return
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/projects/?skip=0&limit=100" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`[
  {
    "uuid": "{project_uuid}",
    "name": "My Project",
    "owner_user_uuid": "{user_uuid}",
    "created_at": "2024-01-15T10:00:00Z"
  }
]`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/projects/{project_uuid}"
        title="Get Project"
        description="Retrieves detailed information about a specific project. Users can only access projects they own or are members of."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            project_uuid: { required: true, description: 'The unique identifier of the project' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/projects/{project_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{project_uuid}",
  "name": "My Project",
  "owner_user_uuid": "{user_uuid}",
  "created_at": "2024-01-15T10:00:00Z"
}`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/projects/{project_uuid}/sopGenerationTasks"
        title="Create SOP Generation Task"
        description="Asynchronously starts the multi-agent pipeline to generate a new SOP. Returns a task UUID to poll for completion. The generated SOP will be associated with this project."
        tags={['Authentication: Required', 'Async: Yes']}
        parameters={{
          path: {
            project_uuid: { required: true, description: 'The unique identifier of the project' }
          }
        }}
        requestBody={{
          source_namespace_uuid: "{namespace_uuid}",
          prompt: "Create an SOP for handling customer support escalations"
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "source_namespace_uuid": "{namespace_uuid}",
  "prompt": "Create an SOP for handling customer support escalations"
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>source_namespace_uuid</strong> (required): UUID of the namespace containing documents to use for SOP generation<br/>
              <strong>prompt</strong> (required): The prompt describing what SOP to generate
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source_namespace_uuid": "{namespace_uuid}",
    "prompt": "Create an SOP for handling customer support escalations"
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (202 Accepted):</h4>
            <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "PENDING",
  "prompt": "Create an SOP for handling customer support escalations",
  "result": null,
  "output_sop_uuid": null
}`} />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200">
              <strong>Note:</strong> SOP generation is asynchronous and can take several minutes. Use the returned
              <code className="bg-blue-900/50 px-1 py-0.5 rounded">uuid</code> (task UUID) to poll the task status endpoint.
              Once completed, the <code className="bg-blue-900/50 px-1 py-0.5 rounded">output_sop_uuid</code> will contain the generated SOP UUID.
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/projects/{project_uuid}/sopGenerationTasks/{task_uuid}"
        title="Get SOP Generation Task"
        description="Retrieves the status and progress of a SOP generation task. Includes structured progress information with stage, progress percentage, and current step details."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            project_uuid: { required: true, description: 'The unique identifier of the project' },
            task_uuid: { required: true, description: 'The unique identifier of the SOP generation task' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "Drafting content: 3/10 sections completed. Current: 'Customer Escalation Process'",
  "prompt": "Create an SOP for handling customer support escalations",
  "result": null,
  "output_sop_uuid": "{sop_uuid}",
  "progress": {
    "stage": "DRAFTING",
    "current_step": 3,
    "total_steps": 10,
    "section_name": "Customer Escalation Process",
    "message": "Drafting content: 3/10 sections completed. Current: 'Customer Escalation Process'",
    "progress_percentage": 25.0
  }
}`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Task Status Stages:</h4>
            <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside ml-2">
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">PENDING</code> - Task queued, not yet started</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">RECONNAISSANCE</code> - Analyzing knowledge graph</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">PLANNING</code> - Strategic planning phase</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">DRAFTING</code> - Content creation phase</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">CRITIQUING</code> - Validation and critique phase</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">REFINING</code> - Refinement phase</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">FINALIZING</code> - Final compilation</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">COMPLETED</code> - SOP generation finished</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">FAILED</code> - Generation encountered an error</li>
            </ul>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="PATCH"
        path="/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/cancel"
        title="Cancel SOP Generation Task"
        description="Cancels a SOP generation task if it's still running. Only PENDING or PROCESSING tasks can be cancelled."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            project_uuid: { required: true, description: 'The unique identifier of the project' },
            task_uuid: { required: true, description: 'The unique identifier of the SOP generation task to cancel' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X PATCH "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/cancel" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "CANCELLED",
  "prompt": "Create an SOP for handling customer support escalations",
  "result": null,
  "output_sop_uuid": null
}`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="PATCH"
        path="/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/pause"
        title="Pause SOP Generation Task"
        description="Pauses a running SOP generation task. The task can be resumed later using the resume endpoint."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            project_uuid: { required: true, description: 'The unique identifier of the project' },
            task_uuid: { required: true, description: 'The unique identifier of the SOP generation task to pause' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X PATCH "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/pause" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "PAUSED",
  "prompt": "Create an SOP for handling customer support escalations",
  "result": null,
  "output_sop_uuid": null
}`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="PATCH"
        path="/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/resume"
        title="Resume SOP Generation Task"
        description="Resumes a paused SOP generation task. The task will continue from where it was paused."
        tags={['Authentication: Required', 'Async: Yes']}
        parameters={{
          path: {
            project_uuid: { required: true, description: 'The unique identifier of the project' },
            task_uuid: { required: true, description: 'The unique identifier of the SOP generation task to resume' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X PATCH "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/resume" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "PROCESSING",
  "prompt": "Create an SOP for handling customer support escalations",
  "result": null,
  "output_sop_uuid": null
}`} />
          </div>
        </div>
      </EndpointCard>

      <div className="mt-8 mb-6">
        <h3 className="text-xl font-semibold text-gray-200 mb-4">Project Sharing & Member Management</h3>
        <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
          Projects support role-based sharing with two roles: <strong>owner</strong> and <strong>member</strong>.
          Owners can add and remove members, while members have read and write access to project resources.
          Only project owners can manage members.
        </p>
      </div>

      <EndpointCard
        method="GET"
        path="/projects/{project_uuid}/members"
        title="List Project Members"
        description="Retrieves all members of a project, including the owner. Returns user information and their roles."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            project_uuid: { required: true, description: 'The unique identifier of the project' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/projects/{project_uuid}/members" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`[
  {
    "user_uuid": "{owner_uuid}",
    "user_email": "owner@example.com",
    "role": "owner",
    "created_at": "2024-01-15T10:00:00Z"
  },
  {
    "user_uuid": "{member_uuid}",
    "user_email": "member@example.com",
    "role": "member",
    "created_at": "2024-01-16T14:30:00Z"
  }
]`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/projects/{project_uuid}/members"
        title="Add Project Member"
        description="Adds a user as a member to a project. Only project owners can add members. The user must exist in the system (identified by email)."
        tags={['Authentication: Required', 'Owner Only']}
        parameters={{
          path: {
            project_uuid: { required: true, description: 'The unique identifier of the project' }
          }
        }}
        requestBody={{
          user_email: "member@example.com",
          role: "member"
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "user_email": "member@example.com",
  "role": "member"
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>user_email</strong> (required): Email address of the user to add as a member<br/>
              <strong>role</strong> (optional, default: "member"): Role to assign. Must be "owner" or "member".
              Note: Cannot assign "owner" role via this endpoint - project ownership must be transferred separately.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/projects/{project_uuid}/members" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_email": "member@example.com",
    "role": "member"
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (201 Created):</h4>
            <CodeBlock code={`{
  "user_uuid": "{member_uuid}",
  "user_email": "member@example.com",
  "role": "member",
  "created_at": "2024-01-16T14:30:00Z"
}`} />
          </div>

          <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
            <p className="text-sm text-yellow-200">
              <strong>Note:</strong> Only project owners can add members. If the user doesn't exist, you'll receive a 400 error.
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="DELETE"
        path="/projects/{project_uuid}/members/{user_uuid}"
        title="Remove Project Member"
        description="Removes a user from a project. Only project owners can remove members. The project owner cannot be removed via this endpoint."
        tags={['Authentication: Required', 'Owner Only']}
        parameters={{
          path: {
            project_uuid: { required: true, description: 'The unique identifier of the project' },
            user_uuid: { required: true, description: 'The unique identifier of the user to remove' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X DELETE "${BASE_URL}/projects/{project_uuid}/members/{user_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (204 No Content):</h4>
            <p className="text-sm text-gray-400">No response body for successful deletion.</p>
          </div>

          <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
            <p className="text-sm text-yellow-200">
              <strong>Note:</strong> Only project owners can remove members. You cannot remove the project owner via this endpoint.
            </p>
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default Projects;
